import React, { useState, ChangeEvent, useCallback } from 'react';
import {
    Box,
    Typography,
    FormControl,
    FormLabel,
    Stack,
    IconButton,
    TextField,
    Button,
    Autocomplete,
    Divider,
    Grid,
    Chip,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import {
    IContest,
    TContestPhase,
    TContestGenre,
    ITechnicalSpec,
    TProjectFormat,
    TPhase,
    SubmissionCategory,
    IProject,
    TContestRefundPolicy,
} from '../../../../types/global/helper';
import { RichTextEditor } from '../../../Common/RichTextEditor';
import { CustomTextField } from '../../../TextInput/CustomTextField';
import { CustomizedMultipleInput } from '../../../CustomizedMultipleInput/CustomizedMultipleInput';
import { CustomizedSwitches } from '../../../Switch/Switch';
import { ContestPhaseSelector } from '../../../EsForm/ContestPhaseSelector';
import { PhotoUploadedWithCropAndCompress } from '../../../EsForm/ProjectThumbnailUpload';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useSortable, SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PhaseData } from '../../../EsForm/ContestPhaseTab';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import MountainIcon from '../../../CustomSVGIcons/MountainIcon';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditRounded from '@mui/icons-material/EditRounded';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Cancel from '@mui/icons-material/Cancel';
import { CustomChipGroup } from '../../../Chip/CustomChipGroup';
import { RichTextViewer } from '../../../Common/RichTextViewer';
import { ThemeStyle } from '../../../../module/ThemeManager';
import { isEmpty, isEmptyValue } from '../../../../utils/lodash';
import { delay } from '../../../../utils/AppUtils';
import { CatField, SubmissionCategoryDisplay } from './SubmissionCategoryDisplay';
import { UploadContestAvatarGroup } from '../../../EsForm/UploadContestAvatarGroup';
import MediaAdderComponent from '../../../MediaAdderComponent/MediaAdderComponent';
import { ProjectPlayer } from '../../../EsCards/ProjectPlayer';

const CONTEST_RATING = ['G', 'PG', 'PG-13', 'R', 'NC-17', 'NR', 'NR-17'];
const CONTEST_VOTING_TYPE = ['daily', 'weekly', 'one-off', 'hourly', 'monthly'];
let CONTEST_PHASE = [
    'preparation',
    'submission',
    'ideation',
    'pitch',
    'production',
    'judging',
    'none',
    'closed',
    'event',
    'registration',
];
const CONTEST_TAGS = [
    'comedy',
    'dialogue',
    'action',
    'monologue',
    'music video',
    'drama',
    'script',
    'vfx',
    'short film',
    'feature film',
    'sci-fi',
    'none',
    'collaboration',
    'advertisement',
    'generic',
    'short',
    'feature',
    'skit',
    'impressions',
    'dance',
    'poetry',
    'make up',
    'rap',
    'music',
    'fashion',
    'festival',
];
const CONTEST_GENRE = [
    'education',
    'edutainment',
    'open air casting',
    'acting',
    'creative advert',
    'tv advertisement',
    'expressions',
    'music',
    'artistic expression',
    'film festival',
    'music festival',
];
// const CURRENCIES = ['USD', 'NGN', 'EUR', 'GBP'];
const PROJECT_FORMATS: TProjectFormat[] = ['vertical', 'horizontal'];

// Fee configuration

// Utility function for fee breakdown calculation

interface DisabledField {
    disabled: boolean;
    message?: string;
}

interface ContestDetailsTabProps {
    contest: IContest;
    onEditDetails: (sectionName: string) => void;
    onSaveChanges?: (payload: Record<string, any>) => void;
    onDeleteCategoryAward?: (index: number, awardId: string) => void;
    onVideoClick?: (video: IProject) => void;
    disabled?: {
        title?: DisabledField;
        description?: DisabledField;
        requirements?: DisabledField;
        score_criteria?: DisabledField;
        rating?: DisabledField;
        contest_video?: DisabledField;
        voting_settings?: DisabledField;
        tags?: DisabledField;
        contest_phases?: DisabledField;
        contest_phase?: DisabledField;
        mobile_feature_image?: DisabledField;
        technical_spec?: DisabledField;
        timezone?: DisabledField;
        fee_settings?: DisabledField;
        contest_phase_dates?: DisabledField;
        fee_structure?: DisabledField;
        submission_state?: DisabledField;
        genre?: DisabledField;
        additional_settings?: DisabledField;
        categories?: DisabledField;
        contest_reference?: DisabledField;
        playlist?: DisabledField;
        host?: DisabledField;
        partner?: DisabledField;
        preselection_enabled?: DisabledField;
        entry_type?: DisabledField;
        refund_policy?: DisabledField;
    };
}

interface SectionHeaderProps {
    title: string;
    hint?: string;
    section: string;
    isEditing: boolean;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    canSave?: boolean;
    disabled?: DisabledField;
}

interface CustomTooltipProps {
    text: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ text }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const { themeStyle } = useThemeContext();

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
            <IconButton
                size="small"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                sx={{
                    padding: 0.5,
                    color: theme[themeStyle].tag_white,
                    '&:hover': {
                        color: theme[themeStyle].highlight,
                    },
                }}>
                <HelpOutlineIcon fontSize="small" />
            </IconButton>
            {showTooltip && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        mb: 0.5,
                        backgroundColor: theme[themeStyle].form_black,
                        color: theme[themeStyle].tag_white,
                        padding: 1,
                        borderRadius: 1,
                        zIndex: 1000,
                        minWidth: 200,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderWidth: '6px 6px 0 6px',
                            borderStyle: 'solid',
                            borderColor: `${theme[themeStyle].form_black} transparent transparent transparent`,
                        },
                    }}>
                    <Typography sx={{ fontSize: '0.875rem', fontFamily: FONT_ENUM.MAIN_REGULAR }}>{text}</Typography>
                </Box>
            )}
        </Box>
    );
};

const SectionHeader: React.FC<SectionHeaderProps> = ({
    title,
    hint,
    section,
    isEditing,
    onEdit,
    onSave,
    onCancel,
    canSave = true,
    disabled,
}) => {
    const { themeStyle } = useThemeContext();

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.25rem',
                        color: theme[themeStyle].tag_white,
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    {title}
                </Typography>
                {hint && <CustomTooltip text={hint} />}
                {disabled?.disabled && disabled.message && <CustomTooltip text={disabled.message} />}
            </Box>
            {isEditing ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={onCancel}
                        sx={{
                            borderColor: theme[themeStyle].placeholderGray,
                            color: theme[themeStyle].primary_white,
                            fontSize: '0.875rem',
                            '&:hover': {
                                borderColor: theme[themeStyle].placeholderGray,
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutline sx={{ color: theme[themeStyle].black }} />}
                        onClick={onSave}
                        disabled={!canSave}
                        sx={{
                            backgroundColor: canSave ? theme[themeStyle].highlight : theme[themeStyle].placeholderGray,
                            color: theme[themeStyle].black,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: canSave
                                    ? theme[themeStyle].primary_orange
                                    : theme[themeStyle].placeholderGray,
                                opacity: 0.9,
                            },
                        }}>
                        Save
                    </Button>
                </Box>
            ) : (
                <IconButton
                    onClick={disabled?.disabled ? undefined : onEdit}
                    disabled={disabled?.disabled}
                    sx={{
                        color: disabled?.disabled
                            ? theme[themeStyle].placeholderGray
                            : theme[themeStyle].primary_orange,
                        cursor: disabled?.disabled ? 'not-allowed' : 'pointer',
                        '&:hover': {
                            color: disabled?.disabled
                                ? theme[themeStyle].placeholderGray
                                : theme[themeStyle].primary_orange,
                        },
                    }}>
                    <EditRounded />
                </IconButton>
            )}
        </Box>
    );
};

const NoContentMessage = ({ message }: { message?: string }) => {
    const { themeStyle } = useThemeContext();
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                color: theme[themeStyle].light_gray_text,
                fontStyle: 'italic',
            }}>
            {message || ' No content provided'}
        </Typography>
    );
};

const DraggableRequirementItem = ({
    req,
    index,
    themeStyle,
    onRemove,
}: {
    req: string;
    index: number;
    themeStyle: ThemeStyle;
    onRemove: () => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: req });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                p: 1.5,
                backgroundColor: theme[themeStyle].gray_text,
                borderRadius: '8px',
                border: `1px solid ${theme[themeStyle].placeholderGray50}`,
                '&:hover': { backgroundColor: theme[themeStyle].form_black },
            }}>
            <Box
                {...listeners}
                {...attributes}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1,
                    cursor: 'grab',
                    color: theme[themeStyle].tag_white,
                    '&:active': { cursor: 'grabbing' },
                }}>
                <DragIndicatorIcon />
            </Box>
            <Typography
                sx={{
                    flex: 1,
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                }}>
                {req}
            </Typography>
            <IconButton
                onClick={onRemove}
                size="small"
                sx={{
                    color: theme[themeStyle].tag_white,
                    '&:hover': { color: theme[themeStyle].highlight },
                }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
};

/**
 * ContestDetailsTab Component
 *
 * @param disabled - Optional object to disable specific sections with custom messages
 * @example
 * <ContestDetailsTab
 *   contest={contest}
 *   onEditDetails={handleEdit}
 *   disabled={{
 *     title: { disabled: true, message: "Title cannot be changed after contest starts" },
 *     categories: { disabled: true, message: "Categories are locked during submission phase" },
 *     host: { disabled: false }
 *   }}
 * />
 */
export const ContestDetailsTab: React.FC<ContestDetailsTabProps> = ({
    contest,
    onEditDetails,
    onSaveChanges,
    onDeleteCategoryAward,
    disabled = {},
    onVideoClick,
}) => {
    const { themeStyle } = useThemeContext();
    const sensors = useSensors(useSensor(PointerSensor));
    const [media, setMedia] = useState<IProject[]>([]);
    const [editingSections, setEditingSections] = useState<{ [key: string]: boolean }>({
        title: false,
        description: false,
        requirements: false,
        score_criteria: false,
        rating: false,
        contest_video: false,
        voting_settings: false,
        tags: false,
        contest_phases: false,
        contest_phase: false,
        mobile_feature_image: false,
        technical_spec: false,
        timezone: false,
        fee_settings: false,
        contest_phase_dates: false,
        fee_structure: false,
        submission_state: false,
        genre: false,
        additional_settings: false,
        categories: false,
        contest_reference: false,
        playlist: false,
        host: false,
        partner: false,
        preselection_enabled: false,
        entry_type: false,
        refund_policy: false,
    });
    const [editingValue, setEditingValue] = useState<Record<string, any>>({});
    const [requirements, setRequirements] = useState<string[]>(contest?.requirements || []);
    const [newRequirement, setNewRequirement] = useState('');
    const [scoreCriteria, setScoreCriteria] = useState<string[]>(contest?.score_criteria || []);
    const [newScoreCriteria, setNewScoreCriteria] = useState('');
    const [votingRequired, setVotingRequired] = useState(contest.voting_required || false);
    const [votingType, setVotingType] = useState(contest.voting_type || 'daily');
    const [preselectionEnabled, setPreselectionEnabled] = useState(contest.preselection_enabled || false);
    const [entryType, setEntryType] = useState(contest.entry_type || 'multiple');
    const [contestPhaseDates, setContestPhaseDates] = useState<PhaseData[]>(
        contest.contest_phase_dates?.map((phase, index) => ({
            phase: phase.phase as TPhase,
            date_started: phase.date_started,
            expiration_date: phase.expiration_date,
            index,
        })) || [],
    );
    const [contestPhase, setContestPhase] = useState<TContestPhase>(contest.contest_phase || 'preparation');
    const [contestRefundPloicy, setContestRefundPolicy] = useState<TContestRefundPolicy>(
        contest.refund_policy || 'refundable during submission',
    );
    const [submissionState, setSubmissionState] = useState<'public_submission' | 'private_submission'>(
        'public_submission',
    );
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    const [tags, setTags] = useState<string[]>(contest.tag || []);
    const [genres, setGenres] = useState<TContestGenre[]>(contest.genre || []);
    const [technicalSpec, setTechnicalSpec] = useState<ITechnicalSpec>(
        contest.technical_spec || {
            min_duration: 0,
            max_duration: 0,
            format: '16:9',
            media_type: 'video',
            project_format: 'horizontal',
        },
    );

    const hasVotePhase = contestPhaseDates.filter(phase => phase.phase === 'vote');

    const menuItems = React.useMemo(() => {
        return [
            ...CONTEST_PHASE,
            ...(submissionState === 'public_submission' && !isEmptyValue(hasVotePhase) ? ['vote'] : []),
        ];
    }, [submissionState, hasVotePhase]);

    const [categories, setCategories] = useState<
        Array<{
            category_name: string;
            category_description: string;
            with_fee: boolean;
            fee?: number;
            currency: string;
        }>
    >([]);

    const handleStartEditing = (section: string) => {
        setEditingSections(prev => {
            const updated: { [key: string]: boolean } = {};

            // Set all keys to false
            for (const key in prev) {
                updated[key] = false;
                const { [key]: _, ...rest } = editingValue;
                setEditingValue(rest);
            }

            // Activate the selected section
            if (section in updated) {
                updated[section] = true;
            }

            return updated;
        });

        // Handle special cases for host and partner fields
        if (section === 'host') {
            setEditingValue(prev => ({
                ...prev,
                [section]: contest.host || null,
            }));
        } else if (section === 'partner') {
            setEditingValue(prev => ({
                ...prev,
                [section]: contest.partner || [],
            }));
        } else {
            setEditingValue(prev => ({
                ...prev,
                [section]: contest[section === 'tags' ? 'tag' : (section as keyof IContest)],
            }));
        }
    };

    const handleCancelEditing = (section: string) => {
        setEditingSections({ ...editingSections, [section]: false });
        const { [section]: _, ...rest } = editingValue;
        setEditingValue(rest);
    };

    const isCategoryValid = (category: any) => {
        return (
            category &&
            category.category_name &&
            category.category_name.trim() !== '' &&
            category.category_description &&
            category.category_description.trim() !== '' &&
            (category.with_fee ? category.fee && category.fee > 0 && category.currency : true)
        );
    };

    const handleOnChange = (field: string, value: any) => {
        setEditingValue(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSaveChanges = async (section: string) => {
        // Check for validation errors before saving
        if (validationErrors.contest_reference || validationErrors.playlist) {
            return; // Don't save if there are validation errors
        }

        if (section === 'categories' && editingValue.categories && editingSections.categories) {
            // Validate that all categories have required fields
            const isValid = editingValue.categories.every(isCategoryValid);

            if (!isValid) {
                return; // Don't save if validation fails
            }

            // Filter out categories with missing required fields
            const validCategories = editingValue.categories.filter(isCategoryValid);
            setCategories(validCategories);
            onEditDetails('categories');
            onSaveChanges && onSaveChanges({ categories: validCategories });

            await delay(1000);
        } else {
            /** Handle On Change here */
            const parsedPayload: any = {};
            Object.keys(editingSections).forEach(key => {
                if (key === 'tags') {
                    parsedPayload['tag'] = editingValue['tag'];
                } else if (editingSections[key]) {
                    parsedPayload[key] = editingValue[key];
                }
            });
            onSaveChanges && onSaveChanges(parsedPayload);
        }
        /** Reset the value */
        setEditingSections({ ...editingSections, [section]: false });
    };

    const handleAddRequirement = useCallback(() => {
        if (newRequirement.trim()) {
            setRequirements([...requirements, newRequirement.trim()]);
            handleOnChange('requirements', [...requirements, newRequirement.trim()]);
            setNewRequirement('');
        }
    }, [newRequirement, requirements]);

    const handleAddScoreCriteria = useCallback(() => {
        if (newScoreCriteria.trim()) {
            setScoreCriteria([...scoreCriteria, newScoreCriteria.trim()]);
            handleOnChange('score_criteria', [...scoreCriteria, newScoreCriteria.trim()]);
            setNewScoreCriteria('');
        }
    }, [newScoreCriteria, scoreCriteria]);

    const handlePhaseChange = (
        phase: { phase: string; date_started: string; expiration_date: string },
        replace?: boolean,
        index?: number,
    ) => {
        const updatedPhase: PhaseData = {
            phase: phase.phase as TPhase,
            date_started: phase.date_started,
            expiration_date: phase.expiration_date,
        };
        if (replace && index) {
            /** find the current phase */
            const currentPhases = [...contestPhaseDates];
            currentPhases[index] = updatedPhase;
            setContestPhaseDates(currentPhases);
            handleOnChange('contest_phase_dates', currentPhases);
        } else {
            setContestPhaseDates([...contestPhaseDates, updatedPhase]);
            handleOnChange('contest_phase_dates', [...contestPhaseDates, updatedPhase]);
        }
        onEditDetails('contest_phase_dates');
    };

    const handlePhaseRemove = (phase: PhaseData, index?: number) => {
        if (index !== undefined) {
            const updatedPhaseDates = contestPhaseDates.filter((_, i) => i !== index);
            setContestPhaseDates(updatedPhaseDates);
            onEditDetails('contest_phase_dates');
            handleOnChange('contest_phase_dates', updatedPhaseDates);
        }
    };

    const handleTechnicalSpecChange = (field: keyof ITechnicalSpec, value: any) => {
        const updatedSpec = { ...technicalSpec, [field]: value };
        setTechnicalSpec(updatedSpec);
        handleOnChange('technical_spec', updatedSpec);
        onEditDetails('technical_spec');
    };

    const handleAddCategory = () => {
        setEditingValue(prev => ({
            ...prev,
            categories: [
                ...editingValue.categories,
                {
                    category_name: '',
                    category_description: '',
                    with_fee: false,
                    fee: 0,
                    currency: 'USD',
                },
            ],
        }));
        onEditDetails('categories');
    };

    const handleCategoryChange = (index: number, field: CatField, value: string | boolean | number) => {
        const newCategories = [...editingValue.categories];
        newCategories[index] = {
            ...newCategories[index],
            [field]: value,
        };

        setCategories(newCategories);
        handleOnChange('categories', newCategories);
        onEditDetails('categories');
    };

    const validatePlaylistReference = useCallback((field: string, values: string[]) => {
        const errors: { [key: string]: string } = {};

        if (field === 'contest_reference') {
            const invalidReferences = values.filter(value => value && !value.startsWith('PR-'));
            if (invalidReferences.length > 0) {
                errors.contest_reference = 'All references must start with "PR-", Ensure the video exists and public';
            }
        } else if (field === 'playlist') {
            const invalidPlaylists = values.filter(value => value && !value.startsWith('PL-'));
            if (invalidPlaylists.length > 0) {
                errors.playlist = 'All playlists must start with "PL-", Ensure the playlist exists and available';
            }
        }

        setValidationErrors(prev => ({
            ...prev,
            ...errors,
        }));

        return Object.keys(errors).length === 0;
    }, []);

    const clearValidationError = useCallback((field: string) => {
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
    }, []);

    return (
        <Box sx={{ py: 3 }}>
            {/* Title */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Title"
                    hint="Enter the title of your contest"
                    section="title"
                    isEditing={editingSections.title}
                    onEdit={() => handleStartEditing('title')}
                    onSave={() => handleSaveChanges('title')}
                    onCancel={() => handleCancelEditing('title')}
                    disabled={disabled.title}
                />
                {!editingSections.title ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].primary_white,
                            pl: 0,
                        }}>
                        {contest.title}
                    </Typography>
                ) : (
                    <Box>
                        <CustomTextField
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onEditDetails('title')}
                            textFieldProps={{
                                InputProps: {
                                    disableUnderline: true,
                                    value: editingValue['title'],
                                    onChange: e => {
                                        onEditDetails('title');
                                        handleOnChange('title', e.target.value);
                                    },
                                    style: {
                                        backgroundColor: theme[themeStyle].secondaryBg,
                                        borderRadius: '8px',
                                        color: theme[themeStyle].lightWhite,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Description */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Description"
                    hint="Provide a detailed description of your contest"
                    section="description"
                    isEditing={editingSections.description}
                    onEdit={() => handleStartEditing('description')}
                    onSave={() => handleSaveChanges('description')}
                    onCancel={() => handleCancelEditing('description')}
                    disabled={disabled.description}
                />
                {!editingSections.description ? (
                    <RichTextViewer theme={themeStyle} content={contest.description} />
                ) : (
                    <Box>
                        <RichTextEditor
                            value={editingValue['description']}
                            onChange={(content: string) => {
                                handleOnChange('description', content);
                                onEditDetails('description');
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Requirements */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Requirements"
                    hint="List the requirements for contest participants"
                    section="requirements"
                    isEditing={editingSections.requirements}
                    onEdit={() => {
                        handleStartEditing('requirements');
                        setRequirements(contest.requirements);
                    }}
                    onSave={() => {
                        handleSaveChanges('requirements');
                    }}
                    onCancel={() => {
                        handleCancelEditing('requirements');
                        setRequirements([]);
                    }}
                    disabled={disabled.requirements}
                />
                {!editingSections.requirements ? (
                    <Box sx={{ pl: 0 }}>
                        {contest.requirements.length > 0 ? (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {contest.requirements.map((requirement, index) => (
                                    <Box
                                        component="li"
                                        key={index}
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            mb: 1,
                                            pl: 0,
                                            '&:last-child': {
                                                mb: 0,
                                            },
                                        }}>
                                        {requirement}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <NoContentMessage message="No requirements provided" />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={event => {
                                const { active, over } = event;
                                if (active.id !== over?.id) {
                                    const oldIndex = requirements.indexOf(active.id as string);
                                    const newIndex = requirements.indexOf(over?.id as string);
                                    const newReqs = arrayMove(requirements, oldIndex, newIndex);
                                    setRequirements(newReqs);
                                    handleOnChange('requirements', newReqs);
                                }
                            }}>
                            <SortableContext items={requirements} strategy={verticalListSortingStrategy}>
                                {requirements.map((req, index) => (
                                    <DraggableRequirementItem
                                        key={req}
                                        req={req}
                                        index={index}
                                        themeStyle={themeStyle}
                                        onRemove={() => {
                                            console.log('updated');
                                            const updated = requirements.filter(r => r !== req);
                                            setRequirements(updated);
                                            handleOnChange('requirements', updated);
                                        }}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <CustomTextField
                                placeholder="Add new requirement"
                                textFieldProps={{
                                    InputProps: {
                                        disableUnderline: true,
                                        value: newRequirement,
                                        onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                            setNewRequirement(e.target.value),
                                        style: {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            borderRadius: '8px',
                                            color: theme[themeStyle].lightWhite,
                                        },
                                        onKeyPress: (e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddRequirement();
                                            }
                                        },
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleAddRequirement}
                                sx={{
                                    ml: 1,
                                    color: theme[themeStyle].tag_white,
                                    '&:hover': {
                                        color: theme[themeStyle].highlight,
                                    },
                                }}>
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Score criteria */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Score Criteria"
                    hint="List the score criteria for voting"
                    section="score_criteria"
                    isEditing={editingSections.score_criteria}
                    onEdit={() => {
                        handleStartEditing('score_criteria');
                        setScoreCriteria(contest.score_criteria ?? []);
                    }}
                    onSave={() => {
                        handleSaveChanges('score_criteria');
                    }}
                    onCancel={() => {
                        handleCancelEditing('score_criteria');
                        setScoreCriteria([]);
                    }}
                    disabled={disabled.score_criteria}
                />
                {!editingSections.score_criteria ? (
                    <Box sx={{ pl: 0 }}>
                        {(contest?.score_criteria ?? []).length > 0 ? (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {(contest?.score_criteria ?? []).map((score_criterion, index) => (
                                    <Box
                                        component="li"
                                        key={index}
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            mb: 1,
                                            pl: 0,
                                            '&:last-child': {
                                                mb: 0,
                                            },
                                        }}>
                                        {score_criterion}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <NoContentMessage message="No score criteria provided" />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={event => {
                                const { active, over } = event;
                                if (active.id !== over?.id) {
                                    const oldIndex = scoreCriteria.indexOf(active.id as string);
                                    const newIndex = scoreCriteria.indexOf(over?.id as string);
                                    const newReqs = arrayMove(scoreCriteria, oldIndex, newIndex);
                                    setScoreCriteria(newReqs);
                                    handleOnChange('score_criretia', newReqs);
                                }
                            }}>
                            <SortableContext items={scoreCriteria} strategy={verticalListSortingStrategy}>
                                {scoreCriteria.map((sc, index) => (
                                    <DraggableRequirementItem
                                        key={sc}
                                        req={sc}
                                        index={index}
                                        themeStyle={themeStyle}
                                        onRemove={() => {
                                            console.log('updated');
                                            const updated = scoreCriteria.filter(r => r !== sc);
                                            setScoreCriteria(updated);
                                            handleOnChange('score_criteria', updated);
                                        }}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <CustomTextField
                                placeholder="Add new score criterion"
                                textFieldProps={{
                                    InputProps: {
                                        disableUnderline: true,
                                        value: newScoreCriteria,
                                        onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                            setNewScoreCriteria(e.target.value),
                                        style: {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            borderRadius: '8px',
                                            color: theme[themeStyle].lightWhite,
                                        },
                                        onKeyPress: (e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddScoreCriteria();
                                            }
                                        },
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleAddScoreCriteria}
                                sx={{
                                    ml: 1,
                                    color: theme[themeStyle].tag_white,
                                    '&:hover': {
                                        color: theme[themeStyle].highlight,
                                    },
                                }}>
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Categories */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Submission Categories"
                    hint="Add categories for contest submissions"
                    section="categories"
                    isEditing={editingSections.categories}
                    onEdit={() => handleStartEditing('categories')}
                    onSave={() => handleSaveChanges('categories')}
                    onCancel={() => {
                        handleCancelEditing('categories');
                        setCategories([]);
                    }}
                    canSave={editingValue && editingValue.categories && editingValue.categories.every(isCategoryValid)}
                    disabled={disabled.categories}
                />
                {!editingSections.categories ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.categories?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.categories?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.categories?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={disabled.categories?.disabled ? undefined : () => handleStartEditing('categories')}>
                        {contest && contest.categories && contest.categories.length > 0 ? (
                            <Grid container spacing={2}>
                                {contest.categories.map((category, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <SubmissionCategoryDisplay
                                            category={{
                                                ...category,
                                                fee: (category.fee as string) ?? 0,
                                            }}
                                            index={index}
                                            isEditing={false}
                                            onCategoryChange={() => {}}
                                            hasSubmissions={false}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <NoContentMessage message="No submission categories provided" />
                        )}
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Grid container spacing={3}>
                            {editingValue &&
                                !isEmpty(editingValue.categories) &&
                                editingValue.categories.map((category: SubmissionCategory, index: number) => (
                                    <Grid item xs={12} key={index}>
                                        <SubmissionCategoryDisplay
                                            category={category}
                                            index={index}
                                            isEditing={true}
                                            onRemoveCategory={() => {
                                                const newCategories = editingValue.categories.filter(
                                                    (_: any, i: number) => i !== index,
                                                );
                                                handleOnChange('categories', newCategories);
                                            }}
                                            onDelete={() => {
                                                const newCategories = categories.filter((_: any, i) => i !== index);
                                                setCategories(newCategories);
                                                onEditDetails('categories');
                                                handleOnChange('categories', newCategories);
                                            }}
                                            onCategoryChange={handleCategoryChange}
                                            hasSubmissions={false}
                                            onDeleteCategoryAward={(index, awardId) => {
                                                if (!onDeleteCategoryAward) return;

                                                onDeleteCategoryAward(index, awardId);

                                                const cloned: SubmissionCategory[] = [...editingValue.categories];
                                                cloned[index] = {
                                                    ...cloned[index],
                                                    category_awards: cloned[index].category_awards?.filter(
                                                        cat => cat._id !== awardId,
                                                    ),
                                                };

                                                handleOnChange('categories', cloned);
                                            }}
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddCategory}
                            sx={{
                                mt: 3,
                                borderColor: theme[themeStyle].primary_orange,
                                color: theme[themeStyle].primary_white,
                                '&:hover': {
                                    borderColor: theme[themeStyle].primary_orange,
                                    backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                },
                            }}>
                            Add Category
                        </Button>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Rating */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Rating"
                    hint="Select the appropriate content rating for your contest"
                    section="rating"
                    isEditing={editingSections.rating}
                    onEdit={() => handleStartEditing('rating')}
                    onSave={() => handleSaveChanges('rating')}
                    onCancel={() => handleCancelEditing('rating')}
                    disabled={disabled.rating}
                />
                {!editingSections.rating ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.rating?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.rating?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.rating?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={disabled.rating?.disabled ? undefined : () => handleStartEditing('rating')}>
                        {contest.rating ? (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                {contest.rating}
                            </Typography>
                        ) : (
                            <NoContentMessage />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Autocomplete
                            options={CONTEST_RATING}
                            value={contest.rating}
                            onChange={(_, newValue) => {
                                onEditDetails('rating');
                                handleOnChange('rating', newValue);
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_black,
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Contest Video */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Campaign Video"
                    hint="Add a video that showcases your campaign"
                    section="contest_video"
                    isEditing={editingSections.contest_video}
                    onEdit={() => handleStartEditing('contest_video')}
                    onSave={() => handleSaveChanges('contest_video')}
                    onCancel={() => handleCancelEditing('contest_video')}
                    disabled={disabled.contest_video}
                />
                {!editingSections.contest_video ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.contest_video?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.contest_video?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.contest_video?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.contest_video?.disabled ? undefined : () => handleStartEditing('contest_video')
                        }>
                        {contest.contest_video && contest.contest_video.hls ? (
                            <ProjectPlayer
                                project={contest.contest_video as IProject}
                                onPlay={() => onVideoClick && onVideoClick(contest.contest_video as IProject)}
                            />
                        ) : (
                            <NoContentMessage />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <MediaAdderComponent
                            mediaValues={media}
                            isEdit={false}
                            mediaType={'projects'}
                            maxSelection={1}
                            title=""
                            onDelete={payload => {
                                /** Delete the selection */
                                const selections = media.filter((sel: any) => sel._id !== payload._id);
                                setMedia(selections);
                            }}
                            onSubmit={payload => {
                                setMedia(payload);
                                if (payload.length > 0) {
                                    handleOnChange('contest_video', payload[0]._id);
                                }
                            }}
                            disableAddButton
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Voting Settings */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Voting Settings"
                    hint="Configure how voting will work for your contest"
                    section="voting_settings"
                    isEditing={editingSections.voting_settings}
                    onEdit={() => handleStartEditing('voting_settings')}
                    onSave={() => handleSaveChanges('voting_settings')}
                    onCancel={() => handleCancelEditing('voting_settings')}
                    disabled={disabled.voting_settings}
                />
                {!editingSections.voting_settings ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.voting_settings?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.voting_settings?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.voting_settings?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.voting_settings?.disabled ? undefined : () => handleStartEditing('voting_settings')
                        }>
                        {votingRequired ? (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                {`Voting Type: ${votingType}`}
                            </Typography>
                        ) : (
                            <NoContentMessage />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <CustomizedSwitches
                                label="Voting Required"
                                switchProps={{
                                    checked: votingRequired ?? false,
                                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                                        setVotingRequired(e.target.checked);
                                        onEditDetails('voting_required');
                                        handleOnChange('voting_required', e.target.checked);
                                    },
                                }}
                            />
                            {votingRequired && (
                                <Autocomplete
                                    options={CONTEST_VOTING_TYPE}
                                    value={votingType}
                                    onChange={(_, newValue) => {
                                        setVotingType(newValue || 'daily');
                                        onEditDetails('voting_type');
                                        handleOnChange('voting_type', newValue || 'daily');
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            sx={{
                                                width: 200,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].placeholderGray50,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].placeholderGray50,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                },
                                            }}
                                        />
                                    )}
                                    ListboxProps={{
                                        sx: {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& .MuiAutocomplete-option': {
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].form_black,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Entry Type Settings */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Entry Type"
                    hint="Configure how participants can submit entries to your contest"
                    section="entry_type"
                    isEditing={editingSections.entry_type}
                    onEdit={() => handleStartEditing('entry_type')}
                    onSave={() => handleSaveChanges('entry_type')}
                    onCancel={() => handleCancelEditing('entry_type')}
                    disabled={disabled.entry_type}
                />
                {!editingSections.entry_type ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.entry_type?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.entry_type?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.entry_type?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={disabled.entry_type?.disabled ? undefined : () => handleStartEditing('entry_type')}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {entryType === 'single' ? 'Single Entry' : 'Multiple Entries'}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Autocomplete
                            options={['single', 'multiple']}
                            value={entryType}
                            onChange={(_, newValue) => {
                                const value = (newValue || 'multiple') as 'single' | 'multiple';
                                setEntryType(value);
                                onEditDetails('entry_type');
                                handleOnChange('entry_type', value);
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={{
                                        width: 200,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_black,
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Pre-selection Settings */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Pre-selection Settings"
                    hint="Enable pre-selection to review submissions before assigning to jurors"
                    section="preselection_enabled"
                    isEditing={editingSections.preselection_enabled}
                    onEdit={() => handleStartEditing('preselection_enabled')}
                    onSave={() => handleSaveChanges('preselection_enabled')}
                    onCancel={() => handleCancelEditing('preselection_enabled')}
                    disabled={disabled.preselection_enabled}
                />
                {!editingSections.preselection_enabled ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.preselection_enabled?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.preselection_enabled?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.preselection_enabled?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.preselection_enabled?.disabled
                                ? undefined
                                : () => handleStartEditing('preselection_enabled')
                        }>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {preselectionEnabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <CustomizedSwitches
                            label="Enable Pre-selection"
                            switchProps={{
                                checked: preselectionEnabled ?? false,
                                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                                    setPreselectionEnabled(e.target.checked);
                                    onEditDetails('preselection_enabled');
                                    handleOnChange('preselection_enabled', e.target.checked);
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Tags */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Tags"
                    hint="Add relevant tags to help users find your contest"
                    section="tags"
                    isEditing={editingSections.tags}
                    onEdit={() => handleStartEditing('tags')}
                    onSave={() => handleSaveChanges('tags')}
                    onCancel={() => handleCancelEditing('tags')}
                    disabled={disabled.tags}
                />
                {!editingSections.tags ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.tags?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.tags?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.tags?.disabled ? 'transparent' : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={disabled.tags?.disabled ? undefined : () => handleStartEditing('tags')}>
                        {contest.tag.length > 0 ? <CustomChipGroup tags={tags} /> : <NoContentMessage />}
                    </Box>
                ) : (
                    <Box>
                        <CustomizedMultipleInput
                            autoCompleteOptions={CONTEST_TAGS}
                            initialValue={tags}
                            onChange={(tags: string[]) => {
                                setTags(tags);
                                onEditDetails('tag');
                                handleOnChange('tag', tags);
                            }}
                            style={{
                                backgroundColor: theme[themeStyle].gray_text,
                                borderRadius: '8px',
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Campaign Phases */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Campaign Phases"
                    hint="Set up the different phases of your campaign"
                    section="contest_phase_dates"
                    isEditing={editingSections.contest_phase_dates}
                    onEdit={() => handleStartEditing('contest_phase_dates')}
                    onSave={() => handleSaveChanges('contest_phase_dates')}
                    onCancel={() => handleCancelEditing('contest_phase_dates')}
                    disabled={disabled.contest_phase_dates}
                />
                {!editingSections.contest_phase_dates ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.contest_phase_dates?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.contest_phase_dates?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.contest_phase_dates?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.contest_phase_dates?.disabled
                                ? undefined
                                : () => handleStartEditing('contest_phase_dates')
                        }>
                        {contestPhaseDates ? (
                            <ContestPhaseSelector
                                phases={contestPhaseDates}
                                onChange={handlePhaseChange}
                                onRemovePhase={handlePhaseRemove}
                                readOnly
                            />
                        ) : (
                            <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                {'Contest Phases Disabled'}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <ContestPhaseSelector
                            phases={contestPhaseDates}
                            onChange={handlePhaseChange}
                            onRemovePhase={handlePhaseRemove}
                            onUpdatePhase={(arg, index) => {
                                handlePhaseChange(arg, true, index);
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Current Phase */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Current Phase"
                    hint="Select the current phase of your contest"
                    section="contest_phase"
                    isEditing={editingSections.contest_phase}
                    onEdit={() => handleStartEditing('contest_phase')}
                    onSave={() => handleSaveChanges('contest_phase')}
                    onCancel={() => handleCancelEditing('contest_phase')}
                    disabled={disabled.contest_phase}
                />
                {!editingSections.contest_phase ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.contest_phase?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.contest_phase?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.contest_phase?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.contest_phase?.disabled ? undefined : () => handleStartEditing('contest_phase')
                        }>
                        <Typography sx={{ color: theme[themeStyle].tag_white }}>{contestPhase}</Typography>
                    </Box>
                ) : (
                    <Box>
                        <Autocomplete
                            options={menuItems}
                            value={contestPhase}
                            onChange={(_, newValue) => {
                                setContestPhase((newValue || 'preparation') as TContestPhase);
                                onEditDetails('contest_phase');
                                handleOnChange('contest_phase', newValue);
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_black,
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Mobile Feature Image */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Mobile Feature Image"
                    hint="Upload an image that will be displayed on mobile devices"
                    section="mobile_feature_image"
                    isEditing={editingSections.mobile_feature_image}
                    onEdit={() => handleStartEditing('mobile_feature_image')}
                    onSave={() => handleSaveChanges('mobile_feature_image')}
                    onCancel={() => handleCancelEditing('mobile_feature_image')}
                    disabled={disabled.mobile_feature_image}
                />
                {!editingSections.mobile_feature_image ? (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '200px',
                            height: '200px',
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: theme[themeStyle].form_gray,
                            cursor: disabled.mobile_feature_image?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.mobile_feature_image?.disabled ? 0.6 : 1,
                            '&:hover': {
                                '& .edit-overlay': {
                                    opacity: disabled.mobile_feature_image?.disabled ? 0 : 1,
                                },
                            },
                        }}
                        onClick={
                            disabled.mobile_feature_image?.disabled
                                ? undefined
                                : () => handleStartEditing('mobile_feature_image')
                        }>
                        {contest.mobile_feature_image ? (
                            <>
                                <img
                                    src={contest.mobile_feature_image}
                                    alt="Mobile Feature"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                                <Box
                                    className="edit-overlay"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0,
                                        transition: 'opacity 0.2s ease',
                                    }}>
                                    <Typography sx={{ color: theme[themeStyle].true_white }}>Edit</Typography>
                                </Box>
                            </>
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                }}>
                                <MountainIcon fill={theme[themeStyle].highlight} />
                                <Typography sx={{ color: theme[themeStyle].tag_white }}>Upload Image</Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <PhotoUploadedWithCropAndCompress
                        aspectRatio="square"
                        initialValue={contest.mobile_feature_image}
                        showDisplay
                        iconStyle={{
                            border: `3px solid ${theme[themeStyle].accountGray}`,
                            borderRadius: 1,
                            mt: 1,
                            width: '200px',
                            height: '200px',
                        }}
                        setPreview={(image, file) => {
                            onEditDetails('mobile_feature_image');
                            handleOnChange('mobile_feature_image', [file]);
                            handleOnChange('mobile_feature_image_preview', image);
                        }}
                    />
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Technical Specifications */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Technical Specifications"
                    hint="Set the technical requirements for contest submissions"
                    section="technical_spec"
                    isEditing={editingSections.technical_spec}
                    onEdit={() => handleStartEditing('technical_spec')}
                    onSave={() => handleSaveChanges('technical_spec')}
                    onCancel={() => handleCancelEditing('technical_spec')}
                    disabled={disabled.technical_spec}
                />
                {!editingSections.technical_spec ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.technical_spec?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.technical_spec?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.technical_spec?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.technical_spec?.disabled ? undefined : () => handleStartEditing('technical_spec')
                        }>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        mb: 1,
                                    }}>
                                    Min Duration
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    {technicalSpec.min_duration} minutes
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        mb: 1,
                                    }}>
                                    Max Duration
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    {technicalSpec.max_duration} minutes
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        mb: 1,
                                    }}>
                                    Media Type
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    {technicalSpec.media_type}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        mb: 1,
                                    }}>
                                    Project Format
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    {technicalSpec.project_format}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                ) : (
                    <Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ color: theme[themeStyle].tag_white, mb: 1 }}>
                                        Min Duration (Minutes)
                                    </FormLabel>
                                    <TextField
                                        type="number"
                                        value={technicalSpec.min_duration}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            handleTechnicalSpecChange('min_duration', Number(e.target.value));
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ color: theme[themeStyle].tag_white, mb: 1 }}>
                                        Max Duration (Minutes)
                                    </FormLabel>
                                    <TextField
                                        type="number"
                                        value={technicalSpec.max_duration}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            handleTechnicalSpecChange('max_duration', Number(e.target.value))
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ color: theme[themeStyle].tag_white, mb: 1 }}>Media Type</FormLabel>
                                    <Autocomplete
                                        options={['video', 'audio', 'picture', 'document']}
                                        value={technicalSpec.media_type}
                                        onChange={(_, newValue) => handleTechnicalSpecChange('media_type', newValue)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                }}
                                            />
                                        )}
                                        ListboxProps={{
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiAutocomplete-option': {
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].form_black,
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ color: theme[themeStyle].tag_white, mb: 1 }}>
                                        Project Format
                                    </FormLabel>
                                    <Autocomplete
                                        options={PROJECT_FORMATS}
                                        value={technicalSpec.project_format || 'horizontal'}
                                        onChange={(_, newValue) =>
                                            handleTechnicalSpecChange('project_format', newValue)
                                        }
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                }}
                                            />
                                        )}
                                        ListboxProps={{
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '& .MuiAutocomplete-option': {
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].form_black,
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Submission State */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Submission State"
                    hint="For private submissions, all submissions must be private. For public submissions, participants can choose to make their submissions private or public."
                    section="submission_state"
                    isEditing={editingSections.submission_state}
                    onEdit={() => handleStartEditing('submission_state')}
                    onSave={() => handleSaveChanges('submission_state')}
                    onCancel={() => handleCancelEditing('submission_state')}
                    disabled={disabled.submission_state}
                />
                {!editingSections.submission_state ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.submission_state?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.submission_state?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.submission_state?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.submission_state?.disabled
                                ? undefined
                                : () => handleStartEditing('submission_state')
                        }>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {submissionState === 'private_submission' ? 'Private' : 'Public'}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Autocomplete
                            options={['private_submission', 'public_submission']}
                            value={submissionState}
                            onChange={(_, newValue) => {
                                setSubmissionState(
                                    (newValue || 'public_submission') as 'private_submission' | 'public_submission',
                                );
                                onEditDetails('submission_state');
                                /** Disable this when contest already has submissions */
                                handleOnChange('submission_state', newValue);
                            }}
                            getOptionLabel={option => (option === 'private_submission' ? 'Private' : 'Public')}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_black,
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Genre */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Genre"
                    hint="Select the genre of your contest"
                    section="genre"
                    isEditing={editingSections.genre}
                    onEdit={() => handleStartEditing('genre')}
                    onSave={() => handleSaveChanges('genre')}
                    onCancel={() => handleCancelEditing('genre')}
                    disabled={disabled.genre}
                />
                {!editingSections.genre ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.genre?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.genre?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.genre?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={disabled.genre?.disabled ? undefined : () => handleStartEditing('genre')}>
                        {contest.genre && contest.genre.length > 0 ? (
                            <CustomChipGroup tags={genres} />
                        ) : (
                            <NoContentMessage message="No genre selected" />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Autocomplete
                            multiple
                            options={CONTEST_GENRE}
                            value={editingValue.genre}
                            onChange={(_, newValue) => {
                                setGenres(newValue as TContestGenre[]);
                                onEditDetails('genre');
                                handleOnChange('genre', newValue);
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_black,
                                        },
                                    },
                                },
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option}
                                        {...getTagProps({ index })}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            '& .MuiChip-deleteIcon': {
                                                color: theme[themeStyle].primary_white,
                                                '&:hover': {
                                                    color: theme[themeStyle].highlight,
                                                },
                                            },
                                        }}
                                    />
                                ))
                            }
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Host */}
            {
                <Box sx={{ mb: 4 }}>
                    <SectionHeader
                        title="Host"
                        hint="Select the host of your contest"
                        section="host"
                        isEditing={editingSections.host}
                        onEdit={() => handleStartEditing('host')}
                        onSave={() => handleSaveChanges('host')}
                        onCancel={() => handleCancelEditing('host')}
                        disabled={disabled.host}
                    />
                    {!editingSections.host ? (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                cursor: disabled.host?.disabled ? 'not-allowed' : 'pointer',
                                border: `1px dashed ${theme[themeStyle].placeholderGray50}`,
                                opacity: disabled.host?.disabled ? 0.6 : 1,
                                '&:hover': {
                                    backgroundColor: disabled.host?.disabled
                                        ? 'transparent'
                                        : theme[themeStyle].form_black,
                                    borderColor: disabled.host?.disabled
                                        ? theme[themeStyle].placeholderGray50
                                        : theme[themeStyle].highlight,
                                },
                            }}
                            onClick={disabled.host?.disabled ? undefined : () => handleStartEditing('host')}>
                            {contest.host ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {contest.host.name}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: theme[themeStyle].light_gray_text,
                                        fontStyle: 'italic',
                                    }}>
                                    <AddIcon sx={{ fontSize: 16, color: theme[themeStyle].highlight }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        Click to add a host
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box>
                            <UploadContestAvatarGroup
                                value={editingValue.host || contest.host || undefined}
                                title="Select Host"
                                searchType="host"
                                disableTitle={false}
                                isEditMode={true}
                                onChange={selectedHost => {
                                    handleOnChange('host', selectedHost);
                                }}
                            />
                        </Box>
                    )}
                </Box>
            }

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Partners */}
            {
                <Box sx={{ mb: 4 }}>
                    <SectionHeader
                        title="Partners"
                        hint="Select the partners of your contest"
                        section="partner"
                        isEditing={editingSections.partner}
                        onEdit={() => handleStartEditing('partner')}
                        onSave={() => handleSaveChanges('partner')}
                        onCancel={() => handleCancelEditing('partner')}
                        disabled={disabled.partner}
                    />
                    {!editingSections.partner ? (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                cursor: disabled.partner?.disabled ? 'not-allowed' : 'pointer',
                                border: `1px dashed ${theme[themeStyle].placeholderGray50}`,
                                opacity: disabled.partner?.disabled ? 0.6 : 1,
                                '&:hover': {
                                    backgroundColor: disabled.partner?.disabled
                                        ? 'transparent'
                                        : theme[themeStyle].form_black,
                                    borderColor: disabled.partner?.disabled
                                        ? theme[themeStyle].placeholderGray50
                                        : theme[themeStyle].highlight,
                                },
                            }}
                            onClick={disabled.partner?.disabled ? undefined : () => handleStartEditing('partner')}>
                            {contest.partner && contest.partner.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {contest.partner.map((partner: any, index: number) => (
                                        <Chip
                                            key={index}
                                            label={partner.name}
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: theme[themeStyle].light_gray_text,
                                        fontStyle: 'italic',
                                    }}>
                                    <AddIcon sx={{ fontSize: 16, color: theme[themeStyle].highlight }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        Click to add partners
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box>
                            <UploadContestAvatarGroup
                                value={editingValue.partner || contest.partner || []}
                                title="Select Partner(s)"
                                searchType="partner"
                                disableTitle={false}
                                isEditMode={true}
                                onChange={selectedPartners => {
                                    handleOnChange('partner', selectedPartners);
                                }}
                            />
                        </Box>
                    )}
                </Box>
            }

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Contest Reference */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Campaign Reference"
                    hint="Paste a video ID here that campaign participants can follow. Please use the video id, usually starts with PR-"
                    section="contest_reference"
                    isEditing={editingSections.contest_reference}
                    onEdit={() => handleStartEditing('contest_reference')}
                    onSave={() => handleSaveChanges('contest_reference')}
                    onCancel={() => handleCancelEditing('contest_reference')}
                    canSave={!validationErrors.contest_reference}
                    disabled={disabled.contest_reference}
                />
                {!editingSections.contest_reference ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.contest_reference?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.contest_reference?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.contest_reference?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.contest_reference?.disabled
                                ? undefined
                                : () => handleStartEditing('contest_reference')
                        }>
                        {contest.contest_reference && contest.contest_reference.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.contest_reference.map((reference: string, index: number) => (
                                    <Chip
                                        key={index}
                                        label={reference}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <NoContentMessage message="No campaign references provided" />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <CustomizedMultipleInput
                            autoCompleteOptions={[]}
                            initialValue={editingValue.contest_reference || contest.contest_reference || []}
                            onChange={(references: string[]) => {
                                handleOnChange('contest_reference', references);
                                onEditDetails('contest_reference');
                                if (references && references.length > 0) {
                                    validatePlaylistReference('contest_reference', references);
                                } else {
                                    clearValidationError('contest_reference');
                                }
                            }}
                            style={{
                                backgroundColor: theme[themeStyle].gray_text,
                                borderRadius: '8px',
                            }}
                            entyLimit={3}
                        />
                        {validationErrors.contest_reference && (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].primary_red,
                                    fontSize: '0.75rem',
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    mt: 0.5,
                                }}>
                                {validationErrors.contest_reference}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Playlist */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Campaign Playlist"
                    hint="Paste a playlist ID here. Campaign submissions will be shown here. Playlist usually starts with PL-"
                    section="playlist"
                    isEditing={editingSections.playlist}
                    onEdit={() => handleStartEditing('playlist')}
                    onSave={() => handleSaveChanges('playlist')}
                    onCancel={() => handleCancelEditing('playlist')}
                    canSave={!validationErrors.playlist}
                    disabled={disabled.playlist}
                />
                {!editingSections.playlist ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.playlist?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.playlist?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.playlist?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={disabled.playlist?.disabled ? undefined : () => handleStartEditing('playlist')}>
                        {contest.playlist && contest.playlist.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.playlist.map((playlistItem: string, index: number) => (
                                    <Chip
                                        key={index}
                                        label={playlistItem}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <NoContentMessage message="No campaign playlists provided" />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <CustomizedMultipleInput
                            autoCompleteOptions={[]}
                            initialValue={editingValue.playlist || contest.playlist || []}
                            onChange={(playlists: string[]) => {
                                handleOnChange('playlist', playlists);
                                onEditDetails('playlist');
                                if (playlists && playlists.length > 0) {
                                    validatePlaylistReference('playlist', playlists);
                                } else {
                                    clearValidationError('playlist');
                                }
                            }}
                            style={{
                                backgroundColor: theme[themeStyle].gray_text,
                                borderRadius: '8px',
                            }}
                            entyLimit={3}
                        />
                        {validationErrors.playlist && (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].primary_red,
                                    fontSize: '0.75rem',
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    mt: 0.5,
                                }}>
                                {validationErrors.playlist}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].placeholderGray50 }} />

            {/* Refund Policy */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader
                    title="Refund Policy"
                    hint="Select the refund policy of your contest"
                    section="refund_policy"
                    isEditing={editingSections.refund_policy}
                    onEdit={() => handleStartEditing('refund_policy')}
                    onSave={() => handleSaveChanges('refund_policy')}
                    onCancel={() => handleCancelEditing('refund_policy')}
                    disabled={disabled.refund_policy}
                />
                {!editingSections.refund_policy ? (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '8px',
                            cursor: disabled.refund_policy?.disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled.refund_policy?.disabled ? 0.6 : 1,
                            '&:hover': {
                                backgroundColor: disabled.refund_policy?.disabled
                                    ? 'transparent'
                                    : theme[themeStyle].form_black,
                            },
                        }}
                        onClick={
                            disabled.refund_policy?.disabled ? undefined : () => handleStartEditing('refund_policy')
                        }>
                        <Typography sx={{ color: theme[themeStyle].tag_white }}>{contestRefundPloicy}</Typography>
                    </Box>
                ) : (
                    <Box>
                        <Autocomplete
                            options={['non-refundable', 'refundable during submission']}
                            value={contestRefundPloicy}
                            onChange={(_, newValue) => {
                                setContestRefundPolicy(
                                    (newValue || 'refundable during submission') as TContestRefundPolicy,
                                );
                                onEditDetails('refund_policy');
                                handleOnChange('refund_policy', newValue);
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                            )}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_black,
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
